import { useMemo } from 'react';
import type { PlatformReconciliationRow } from '../types/operations.types';
import { formatNumber, formatPercent } from '../helpers/etl-health.helpers';
import { EtlDetailTable } from './etl-detail-table';
import { EtlHealthStatusPill } from './etl-health-status-pill';

type Props = {
  rows: PlatformReconciliationRow[];
  selectedDate: string | null;
};

export function PlatformReconciliationPanel({ rows, selectedDate }: Props) {
  const filteredRows = useMemo(() => {
    if (!selectedDate) {
      return rows;
    }
    return rows.filter((row) => row.check_date === selectedDate);
  }, [rows, selectedDate]);

  const platformOptions = useMemo(
    () => [...new Set(rows.map((row) => row.platform))].sort(),
    [rows],
  );
  const statusOptions = useMemo(
    () => [...new Set(rows.map((row) => row.status))].sort(),
    [rows],
  );

  return (
    <EtlDetailTable
      rows={filteredRows}
      columns={[
        { id: 'date', header: 'Date', accessor: (row) => row.check_date, sortable: true },
        { id: 'platform', header: 'Platform', accessor: (row) => row.platform, sortable: true },
        {
          id: 'account',
          header: 'Account',
          accessor: (row) => row.account_name ?? row.account_id,
          sortable: true,
        },
        { id: 'metric', header: 'Metric', accessor: (row) => row.metric, sortable: true },
        {
          id: 'vendor_total',
          header: 'Vendor total',
          accessor: (row) => row.vendor_total,
          sortable: true,
          align: 'right',
          render: (row) => formatNumber(row.vendor_total),
        },
        {
          id: 'mart_total',
          header: 'Mart total',
          accessor: (row) => row.mart_total,
          sortable: true,
          align: 'right',
          render: (row) => formatNumber(row.mart_total),
        },
        {
          id: 'delta_pct',
          header: 'Delta %',
          accessor: (row) => row.delta_pct,
          sortable: true,
          align: 'right',
          render: (row) => formatPercent(row.delta_pct),
        },
        {
          id: 'threshold_pct',
          header: 'Threshold %',
          accessor: (row) => row.threshold_pct,
          sortable: true,
          align: 'right',
          render: (row) => formatPercent(row.threshold_pct),
        },
        {
          id: 'status',
          header: 'Status',
          accessor: (row) => row.status,
          sortable: true,
          render: (row) => <EtlHealthStatusPill status={row.status} />,
        },
        { id: 'notes', header: 'Notes', accessor: (row) => row.notes ?? '—' },
      ]}
      filters={[
        {
          id: 'platform',
          label: 'Platform',
          options: platformOptions,
          getValue: (row) => row.platform,
        },
        {
          id: 'status',
          label: 'Status',
          options: statusOptions,
          getValue: (row) => row.status,
        },
      ]}
      emptyMessage="No platform reconciliation checks for the selected period."
    />
  );
}
